import { PermissionEnum } from '@Application/Security/Roles';
import { openLogoSidebar } from '@UI/Pages/Administration/Organizations/Signals/logoSidebarSignal';
import { openOrganizationSidebar } from '@UI/Pages/Administration/Organizations/Signals/organizationSidebarSignal';
import { setOrganizationToDelete } from '@UI/Pages/Administration/Organizations/Signals/organizationToDeleteSignal';
import useHasPermission from '@UI/Providers/PermissionProvider/useHasPermission';
import type OrganizationView from '@UI/Store/Organizations/Views/OrganizationView';
import { css } from '@emotion/react';
import { ImageIcon } from 'lucide-react';
import * as React from 'react';
import { Button } from '@verclam/ui-kit';

const actionsRowStyle = css`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	margin-top: auto;
`;

type Props = {
	readonly organization: OrganizationView;
};

/**
 * Leaf action component: it opens the edit sidebar and requests the delete confirmation purely
 * through their signals' setters — edit and delete share the same `ORGANIZATION_WRITE` gate as
 * creation. The outer `div` is this card's own subgrid track, not decorative filler: it stays
 * rendered — empty — without `ORGANIZATION_WRITE`, instead of collapsing the track and misaligning
 * the card against its siblings.
 */
const OrganizationCardActions: React.FC<Props> = ({ organization }) => {
	const canWrite = useHasPermission(PermissionEnum.ORGANIZATION_WRITE);

	const handleEditClick = React.useCallback(() => {
		openOrganizationSidebar(organization.id);
	}, [organization.id]);

	const handleLogoClick = React.useCallback(() => {
		openLogoSidebar({ owner: 'organization', organizationId: organization.id });
	}, [organization.id]);

	const handleDeleteClick = React.useCallback(() => {
		setOrganizationToDelete(organization.id);
	}, [organization.id]);

	if (!canWrite) {
		return <div css={actionsRowStyle} />;
	}

	return (
		<div css={actionsRowStyle}>
			<Button
				type="button"
				size="sm"
				aria-label={`Modifier la structure ${organization.name}`}
				severity={'secondary'}
				onClick={handleEditClick}
			>
				Modifier
			</Button>
			<Button
				type="button"
				size="sm"
				aria-label={`Logo de la structure ${organization.name}`}
				severity={'secondary'}
				icon={<ImageIcon size={14} />}
				onClick={handleLogoClick}
			>
				Logo
			</Button>
			<Button
				type="button"
				size="sm"
				aria-label={`Supprimer la structure ${organization.name}`}
				severity={'danger'}
				onClick={handleDeleteClick}
			>
				Supprimer
			</Button>
		</div>
	);
};

export default OrganizationCardActions;
